import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, functions, httpsCallable } from '../../firebase';
import { fetchUserData, selectUser } from 'features/userSlice';
import { Row, Spinner } from 'reactstrap';
import { trackAppMetric } from 'utils/appMetrics';
import { getSpotifyRedirectUri } from 'utils/oauthRedirectUri';

/**
 * SpotifyCallback
 *
 * Handles the redirect back from Spotify after the user authorizes the app.
 * Spotify redirects to /spotify-callback?code=CODE&state=STATE
 *
 * This component:
 *   1. Extracts the authorization code from the URL
 *   2. Calls the spotifyExchangeToken Cloud Function
 *   3. Redirects to /settings on success
 */
export default function SpotifyCallback() {
  const location  = useLocation();
  const history   = useHistory();
  const dispatch  = useDispatch();
  const user      = useSelector(selectUser);
  const exchanged = useRef(false); // prevent double-firing
  const [status, setStatus]   = useState('loading'); // 'loading' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    console.log('[SpotifyCallback] component mounted');
    console.log('[SpotifyCallback] location.search:', location.search);

    const params = new URLSearchParams(location.search);
    const code   = params.get('code');
    const error  = params.get('error');
    const state  = params.get('state');
    const storedState = sessionStorage.getItem('spotify_oauth_state');

    console.log('[SpotifyCallback] code:', code ? `${code.slice(0, 20)}…` : null);
    console.log('[SpotifyCallback] error param:', error);

    if (error) {
      console.error('[SpotifyCallback] Spotify returned an error:', error);
      trackAppMetric('spotify.connect', {
        outcome: 'declined',
        reason: String(error).slice(0, 64),
      });
      setErrorMsg(`Spotify declined authorization: ${error}`);
      setStatus('error');
      return;
    }

    if (!state || !storedState || state !== storedState) {
      console.error('[SpotifyCallback] OAuth state validation failed');
      trackAppMetric('spotify.connect', {
        outcome: 'state_mismatch',
      });
      setErrorMsg('Spotify connection could not be verified. Please try connecting again.');
      setStatus('error');
      return;
    }

    sessionStorage.removeItem('spotify_oauth_state');

    if (!code) {
      console.error('[SpotifyCallback] No code in URL params');
      trackAppMetric('spotify.connect', {
        outcome: 'missing_code',
      });
      setErrorMsg('No authorization code received from Spotify.');
      setStatus('error');
      return;
    }

    const exchange = async (firebaseUser) => {
      if (exchanged.current) {
        console.log('[SpotifyCallback] exchange already called, skipping');
        return;
      }
      exchanged.current = true;
      console.log('[SpotifyCallback] calling spotifyExchangeToken for uid:', firebaseUser?.uid);
      try {
        const exchangeFn = httpsCallable(functions, 'spotifyExchangeToken');
        const redirectUri = getSpotifyRedirectUri();
        console.log('[SpotifyCallback] redirectUri being sent:', redirectUri);
        await exchangeFn({ code, redirectUri });
        console.log('[SpotifyCallback] token exchange succeeded');
        trackAppMetric('spotify.connect', {
          outcome: 'success',
        });
        // Refresh Redux user state so Settings immediately shows "Connected"
        const uid = firebaseUser?.uid || user?.uid;
        if (uid) {
          console.log('[SpotifyCallback] fetching updated user data for uid:', uid);
          await dispatch(fetchUserData(uid));
        } else {
          console.warn('[SpotifyCallback] no uid available, skipping fetchUserData');
        }
        const returnTarget = sessionStorage.getItem('spotify_oauth_return_to');
        sessionStorage.removeItem('spotify_oauth_return_to');
        const safeReturnTarget = returnTarget && returnTarget.startsWith('/') && !returnTarget.startsWith('//')
          ? returnTarget
          : '/settings#spotify';
        console.log('[SpotifyCallback] redirecting after Spotify connect:', safeReturnTarget);
        history.replace(safeReturnTarget);
      } catch (err) {
        console.error('[SpotifyCallback] spotifyExchangeToken error:', err);
        trackAppMetric('spotify.connect', {
          outcome: 'exchange_error',
        });
        exchanged.current = false; // allow retry on error
        setErrorMsg(err.message || 'Failed to connect Spotify. Please try again.');
        setStatus('error');
      }
    };

    // Wait for Firebase auth to restore the session before calling the function.
    // onAuthStateChanged fires immediately with the current user (or null if signed out).
    console.log('[SpotifyCallback] subscribing to onAuthStateChanged…');
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      console.log('[SpotifyCallback] onAuthStateChanged fired — firebaseUser:', firebaseUser ? firebaseUser.uid : null);
      if (firebaseUser) {
        clearTimeout(authTimeout);
        unsubscribe();
        exchange(firebaseUser);
      } else {
        console.log('[SpotifyCallback] no user yet, waiting for auth to restore…');
      }
    });

    // If auth doesn't restore within 8 seconds the session is on a different origin
    // (e.g. logged in at localhost:3000 but redirected to 127.0.0.1:3000).
    const authTimeout = setTimeout(() => {
      console.error('[SpotifyCallback] auth timed out — likely an origin mismatch');
      trackAppMetric('spotify.connect', {
        outcome: 'auth_timeout',
      });
      unsubscribe();
      setErrorMsg(
        'Could not restore your session. ' +
        'For local testing, make sure you log in at http://127.0.0.1:3000 ' +
        '(not localhost:3000) before connecting Spotify.'
      );
      setStatus('error');
    }, 8000);

    return () => {
      console.log('[SpotifyCallback] cleanup — unsubscribing onAuthStateChanged');
      clearTimeout(authTimeout);
      unsubscribe();
    };
  // Run once on mount; location.search is stable
  // eslint-disable-next-line
  }, []);

  if (status === 'loading') {
    return (
      <Row style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', flexDirection: 'column', gap: '16px' }}>
        <Spinner style={{ height: '3rem', width: '3rem' }} color="info" />
        <p style={{ color: '#004aad', fontWeight: 'bold' }}>Connecting your Spotify account…</p>
      </Row>
    );
  }

  return (
    <Row style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', flexDirection: 'column', gap: '12px', padding: '20px' }}>
      <i className="fa fa-exclamation-circle" style={{ fontSize: '48px', color: '#e74c3c' }} />
      <h4 style={{ color: '#004aad', fontWeight: 'bold' }}>Spotify Connection Failed</h4>
      <p style={{ color: '#666', textAlign: 'center', maxWidth: '400px' }}>{errorMsg}</p>
      <button
        onClick={() => history.replace('/settings#spotify')}
        style={{ marginTop: '8px', padding: '10px 24px', borderRadius: '30px', backgroundColor: '#1db954', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
      >
        Back to Settings
      </button>
    </Row>
  );
}
